// Chart utilities and data aggregation functions for education facility visualizations

// Education data processing functions
export function calculateOwnershipDistribution(institutions) {
  if (!institutions.length) return []

  const distribution = institutions.reduce((acc, institution) => {
    const ownership = institution.ownership || 'Unknown'
    acc[ownership] = (acc[ownership] || 0) + 1
    return acc
  }, {})

  return Object.entries(distribution).map(([name, value]) => ({
    name,
    value,
    percentage: Math.round((value / institutions.length) * 100),
  }))
}

export function calculateEducationLevelDistribution(institutions) {
  if (!institutions.length) return []

  const distribution = institutions.reduce((acc, institution) => {
    const type = institution.institution_type || 'Unknown'
    acc[type] = (acc[type] || 0) + 1
    return acc
  }, {})

  return Object.entries(distribution).map(([name, value]) => ({
    name,
    value,
    percentage: Math.round((value / institutions.length) * 100),
  }))
}

export function calculateParishDistribution(institutions) {
  if (!institutions.length) return []

  const distribution = institutions.reduce((acc, institution) => {
    const parish = institution.parish || 'Unknown'
    acc[parish] = (acc[parish] || 0) + 1
    return acc
  }, {})

  return Object.entries(distribution)
    .map(([name, count]) => ({
      name,
      count,
    }))
    .sort((a, b) => b.count - a.count)
}

export function calculateOwnershipByLevel(institutions) {
  if (!institutions.length) return []

  const data = institutions.reduce((acc, institution) => {
    const type = institution.institution_type || 'Unknown'
    const ownership = institution.ownership || 'Unknown'

    if (!acc[type]) {
      acc[type] = { Government: 0, Private: 0, Unknown: 0 }
    }
    acc[type][ownership] = (acc[type][ownership] || 0) + 1
    return acc
  }, {})

  return Object.entries(data).map(([type, counts]) => ({
    type,
    government: counts.Government || 0,
    private: counts.Private || 0,
    unknown: counts.Unknown || 0,
    total:
      (counts.Government || 0) + (counts.Private || 0) + (counts.Unknown || 0),
  }))
}

export function generateEducationInsights(institutions, locationName) {
  if (!institutions.length) return ['No education data available']

  const insights = []
  const total = institutions.length
  const ownershipDist = calculateOwnershipDistribution(institutions)
  const levelDist = calculateEducationLevelDistribution(institutions)

  const governmentCount =
    ownershipDist.find(d => d.name === 'Government')?.value || 0
  const privateCount = ownershipDist.find(d => d.name === 'Private')?.value || 0
  const primaryCount = levelDist.find(d => d.name === 'Primary')?.value || 0
  const secondaryCount = levelDist.find(d => d.name === 'Secondary')?.value || 0

  const governmentRatio = Math.round((governmentCount / total) * 100)
  const privateRatio = Math.round((privateCount / total) * 100)
  const primaryRatio = Math.round((primaryCount / total) * 100)

  // Location context
  insights.push(`${locationName} has ${total} educational institutions`)

  // Ownership insights
  if (governmentRatio > 70) {
    insights.push(
      `Education is primarily government-provided (${governmentRatio}%)`
    )
  } else if (privateRatio > 50) {
    insights.push(`Strong private education presence (${privateRatio}%)`)
  } else {
    insights.push(`Balanced public-private education mix`)
  }

  // Level insights
  if (primaryRatio > 85) {
    insights.push(`Primarily primary education focused (${primaryRatio}%)`)
  } else if (secondaryCount === 0) {
    insights.push('No secondary schools identified - potential access gap')
  } else {
    const ratio = Math.round(primaryCount / secondaryCount)
    insights.push(`${ratio}:1 primary to secondary school ratio`)
  }

  // Coverage insights
  const subcounties = new Set(institutions.map(i => i.subcounty)).size
  const parishes = new Set(institutions.map(i => i.parish).filter(Boolean)).size

  if (subcounties > 1) {
    insights.push(`Education services span ${subcounties} subcounties`)
  }

  if (parishes > 0) {
    insights.push(`${parishes} parishes have educational facilities`)
  }

  return insights
}

// Education-specific chart creation functions
export function createEducationOwnershipDonutOption(
  ownershipData,
  title,
  centerText
) {
  const colors = {
    Government: '#3B82F6', // Blue
    Private: '#F97316', // Orange
    Unknown: '#9CA3AF', // Gray
  }

  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: '5%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1F2937',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    legend: {
      orient: 'horizontal',
      top: '10',
      itemGap: 20,
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    series: [
      {
        name: 'Ownership',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          label: {
            show: false,
            fontSize: 20,
            fontWeight: 'bold',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
        labelLine: {
          show: false,
        },
        data: ownershipData.map(item => ({
          value: item.value,
          name: item.name,
          itemStyle: {
            color: colors[item.name] || '#9CA3AF',
          },
        })),
      },
      {
        type: 'pie',
        radius: ['0%', '35%'],
        center: ['50%', '50%'],
        silent: true,
        label: {
          show: false,
          position: 'center',
          formatter: centerText,
          fontSize: 14,
          fontWeight: 'bold',
          color: '#1F2937',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        data: [{ value: 1, itemStyle: { color: 'transparent' } }],
      },
    ],
    graphic: {
      type: 'text',
      left: 'center',
      top: 'center',
      style: {
        text: centerText,
        textAlign: 'center',
        fill: '#1F2937',
        fontSize: 17,
        fontWeight: 600,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
  }
}

export function createLocationComparisonOption(locationData, title) {
  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: '5%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1F2937',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: 'Number of Institutions',
      nameTextStyle: {
        padding: [0, 0, 0, 50],
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      axisLabel: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'category',
      data: locationData.map(item => item.name),
      axisLabel: {
        interval: 0,
        rotate: 0,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Institutions',
        type: 'bar',
        data: locationData.map(item => ({
          value: item.count,
          itemStyle: {
            color: '#3B82F6',
            borderRadius: [0, 4, 4, 0],
          },
        })),
        label: {
          show: true,
          position: 'right',
          formatter: '{c}',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
    ],
  }
}

// Student enrollment analysis functions
export function calculateEnrollmentDistribution(institutions) {
  if (!institutions.length) return []

  const enrollmentData = institutions
    .filter(inst => inst.total_learners && inst.total_learners > 0)
    .map(inst => ({
      name: inst.school_name || inst.name,
      district: inst.district,
      subcounty: inst.subcounty,
      total_learners: inst.total_learners,
      boys_count: inst.boys_count || 0,
      girls_count: inst.girls_count || 0,
      disabled_learners: inst.disabled_learners || 0,
      institution_type: inst.institution_type,
      ownership: inst.ownership,
    }))
    .sort((a, b) => b.total_learners - a.total_learners)

  return enrollmentData
}

export function calculateGenderBalance(institutions) {
  if (!institutions.length) return { total: 0, boys: 0, girls: 0, ratio: 0 }

  const totals = institutions
    .filter(inst => inst.boys_count > 0 || inst.girls_count > 0)
    .reduce(
      (acc, inst) => ({
        boys: acc.boys + (inst.boys_count || 0),
        girls: acc.girls + (inst.girls_count || 0),
      }),
      { boys: 0, girls: 0 }
    )

  const total = totals.boys + totals.girls
  const ratio = total > 0 ? (totals.girls / totals.boys).toFixed(2) : 0

  return {
    total,
    boys: totals.boys,
    girls: totals.girls,
    boys_percentage: total > 0 ? Math.round((totals.boys / total) * 100) : 0,
    girls_percentage: total > 0 ? Math.round((totals.girls / total) * 100) : 0,
    ratio,
  }
}

export function calculateTeacherQualifications(institutions) {
  if (!institutions.length) return []

  const qualificationTotals = institutions
    .filter(inst => inst.total_teachers > 0)
    .reduce(
      (acc, inst) => ({
        degree: acc.degree + (inst.degree_teachers || 0),
        diploma: acc.diploma + (inst.diploma_teachers || 0),
        certificate: acc.certificate + (inst.certificate_teachers || 0),
        total: acc.total + (inst.total_teachers || 0),
      }),
      { degree: 0, diploma: 0, certificate: 0, total: 0 }
    )

  if (qualificationTotals.total === 0) return []

  return [
    {
      name: 'Degree',
      value: qualificationTotals.degree,
      percentage: Math.round(
        (qualificationTotals.degree / qualificationTotals.total) * 100
      ),
    },
    {
      name: 'Diploma',
      value: qualificationTotals.diploma,
      percentage: Math.round(
        (qualificationTotals.diploma / qualificationTotals.total) * 100
      ),
    },
    {
      name: 'Certificate',
      value: qualificationTotals.certificate,
      percentage: Math.round(
        (qualificationTotals.certificate / qualificationTotals.total) * 100
      ),
    },
  ]
}

export function calculateInfrastructureReadiness(institutions) {
  if (!institutions.length) return []

  const infrastructureItems = [
    { key: 'electricity_available', name: 'Electricity' },
    { key: 'water_available', name: 'Water Supply' },
    { key: 'ict_lab_available', name: 'ICT Laboratory' },
    { key: 'handwashing_available', name: 'Handwashing Facilities' },
    { key: 'separate_gender_toilets', name: 'Gender-Separated Toilets' },
    { key: 'disability_accessible_toilets', name: 'Accessible Toilets' },
    { key: 'teacher_toilets_available', name: 'Teacher Toilets' },
  ]

  return infrastructureItems.map(item => {
    const available = institutions.filter(
      inst => inst[item.key] === 'Yes'
    ).length
    const total = institutions.length

    return {
      name: item.name,
      available,
      total,
      percentage: total > 0 ? Math.round((available / total) * 100) : 0,
    }
  })
}

export function createGenderBalanceDonutOption(genderData, title) {
  return {
    title: {
      show: false,
      text: title,
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
    },
    legend: {
      bottom: '5%',
      textStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    series: [
      {
        name: 'Gender Distribution',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '18',
            fontWeight: 'bold',
            fontFamily: 'Poppins, sans-serif',
          },
        },
        labelLine: {
          show: false,
        },
        data: [
          {
            value: genderData.boys,
            name: 'Boys',
            itemStyle: { color: '#3B82F6' },
          },
          {
            value: genderData.girls,
            name: 'Girls',
            itemStyle: { color: '#EC4899' },
          },
        ],
      },
    ],
  }
}

export function createInfrastructureBarOption(infrastructureData, title) {
  return {
    title: {
      show: false,
      text: title,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        const data = params[0]
        const item = infrastructureData[data.dataIndex]
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${item.name}</strong><br/>
            Available: ${item.available} schools<br/>
            Coverage: ${item.percentage}%<br/>
            Total schools: ${item.total}
          </div>
        `
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: infrastructureData.map(item => item.name),
      axisLabel: {
        interval: 0,
        rotate: 45,
        fontSize: 10,
        fontFamily: 'Poppins, sans-serif',
      },
    },
    yAxis: {
      type: 'value',
      name: 'Coverage (%)',
      max: 100,
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    series: [
      {
        type: 'bar',
        data: infrastructureData.map(item => item.percentage),
        itemStyle: {
          color: '#10B981',
        },
      },
    ],
  }
}

// Educational Infrastructure Analytics Functions

// Calculate resource ratios for facility capacity analysis
export function calculateResourceRatios(institutions) {
  if (!institutions.length) return null

  const facilitiesWithData = institutions.filter(
    inst =>
      inst.total_learners > 0 &&
      inst.total_teachers > 0 &&
      inst.permanent_classrooms > 0 &&
      inst.total_toilets > 0
  )

  if (!facilitiesWithData.length) return null

  // Calculate averages for location
  const totals = facilitiesWithData.reduce(
    (acc, inst) => {
      return {
        learners: acc.learners + (parseFloat(inst.total_learners) || 0),
        teachers: acc.teachers + (parseFloat(inst.total_teachers) || 0),
        classrooms:
          acc.classrooms + (parseFloat(inst.permanent_classrooms) || 0),
        toilets: acc.toilets + (parseFloat(inst.total_toilets) || 0),
        textbooks: acc.textbooks + (parseFloat(inst.total_learners) || 0), // Assuming 1:1 ratio for now
      }
    },
    { learners: 0, teachers: 0, classrooms: 0, toilets: 0, textbooks: 0 }
  )

  return {
    pupilTeacherRatio: parseFloat(
      (totals.learners / totals.teachers).toFixed(2)
    ),
    pupilClassroomRatio: parseFloat(
      (totals.learners / totals.classrooms).toFixed(2)
    ),
    pupilTextbookRatio: parseFloat(
      (totals.learners / totals.textbooks).toFixed(2)
    ), // Will be 1.0 for now
    pupilToiletRatio: parseFloat((totals.learners / totals.toilets).toFixed(2)),
    totalFacilities: facilitiesWithData.length,
  }
}

// Create resource ratio cards data for dashboard
export function createResourceRatiosCardsData(ratios) {
  if (!ratios) return null

  const cards = [
    {
      name: 'Pupil-Teacher Ratio',
      value: ratios.pupilTeacherRatio,
      benchmark: 40, // UNESCO primary standard
      secondaryBenchmark: 30, // UNESCO secondary standard
      icon: '👩‍🏫',
      description: 'Students per teacher',
      unit: ':1',
    },
    {
      name: 'Pupil-Classroom Ratio',
      value: ratios.pupilClassroomRatio,
      benchmark: 40,
      icon: '🏫',
      description: 'Students per classroom',
      unit: ':1',
    },
    {
      name: 'Pupil-Toilet Ratio',
      value: ratios.pupilToiletRatio,
      benchmark: 30,
      icon: '🚻',
      description: 'Students per toilet',
      unit: ':1',
    },
    {
      name: 'Pupil-Textbook Ratio',
      value: ratios.pupilTextbookRatio,
      benchmark: 1,
      icon: '📚',
      description: 'Students per textbook',
      unit: ':1',
    },
  ]

  return cards.map(card => {
    const status =
      card.value <= card.benchmark
        ? 'excellent'
        : card.value <= card.benchmark * 1.25
          ? 'good'
          : card.value <= card.benchmark * 1.5
            ? 'fair'
            : 'poor'

    const statusColor = {
      excellent: '#10B981', // green
      good: '#3B82F6', // blue
      fair: '#F59E0B', // yellow
      poor: '#EF4444', // red
    }[status]

    const statusText = {
      excellent: 'Excellent',
      good: 'Good',
      fair: 'Needs Attention',
      poor: 'Critical',
    }[status]

    return {
      ...card,
      status,
      statusColor,
      statusText,
      percentage: Math.min((card.benchmark / card.value) * 100, 100),
    }
  })
}

// Calculate teacher-student ratios for individual schools
export function calculateTeacherStudentRatios(institutions) {
  if (!institutions.length) return []

  return institutions
    .filter(inst => inst.total_learners > 0 && inst.total_teachers > 0)
    .map(inst => ({
      name: inst.institution_name || inst.name,
      ratio: parseFloat(
        (
          parseFloat(inst.total_learners) / parseFloat(inst.total_teachers)
        ).toFixed(2)
      ),
      learners: parseFloat(inst.total_learners),
      teachers: parseFloat(inst.total_teachers),
      type: inst.institution_type,
      ownership: inst.ownership,
    }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, 15) // Top 15 schools with highest ratios
}

// Create horizontal bar chart for teacher-student ratios
export function createTeacherStudentRatioBarOption(ratioData) {
  if (!ratioData.length) return null

  return {
    title: {
      show: false,
      text: 'Teacher-Student Ratio Analysis',
      left: 'center',
      top: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1F2937',
        fontFamily: 'Poppins, sans-serif',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        const data = params[0]
        const school = ratioData[data.dataIndex]
        const status =
          school.ratio <= 40
            ? 'Good'
            : school.ratio <= 50
              ? 'Strained'
              : 'Overcrowded'
        const color =
          school.ratio <= 40
            ? '#10B981'
            : school.ratio <= 50
              ? '#F59E0B'
              : '#EF4444'
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${school.name}</strong><br/>
            Ratio: ${school.ratio.toFixed(2)}:1<br/>
            Students: ${school.learners}<br/>
            Teachers: ${school.teachers}<br/>
            Type: ${school.type}<br/>
            <span style="color: ${color};">Status: ${status}</span>
          </div>
        `
      },
    },
    grid: {
      left: '3%',
      right: '10%',
      bottom: '5%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: 'Pupil-Teacher Ratio',
      nameLocation: 'middle',
      nameGap: 25,
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    yAxis: {
      type: 'category',
      data: ratioData.map(school =>
        school.name.length > 25
          ? school.name.substring(0, 25) + '...'
          : school.name
      ),
      axisLabel: {
        fontSize: 10,
        fontFamily: 'Poppins, sans-serif',
      },
    },
    series: [
      {
        type: 'bar',
        data: ratioData.map(school => ({
          value: school.ratio,
          itemStyle: {
            color:
              school.ratio <= 40
                ? '#10B981'
                : school.ratio <= 50
                  ? '#F59E0B'
                  : '#EF4444',
          },
        })),
        label: {
          show: true,
          position: 'right',
          formatter: function (params) {
            return params.value.toFixed(2) + ':1'
          },
          fontFamily: 'Poppins, sans-serif',
          fontSize: 10,
        },
        markLine: {
          data: [
            {
              xAxis: 40,
              lineStyle: { color: '#10B981', type: 'dashed' },
              label: { formatter: 'UNESCO Primary (40:1)' },
            },
            {
              xAxis: 30,
              lineStyle: { color: '#3B82F6', type: 'dashed' },
              label: { formatter: 'UNESCO Secondary (30:1)' },
            },
          ],
        },
      },
    ],
  }
}

// Calculate infrastructure completeness scores
export function calculateInfrastructureScores(institutions) {
  if (!institutions.length) return []

  return institutions
    .map(inst => {
      const components = {
        water: inst.water_available === 'Yes' ? 1 : 0,
        electricity: inst.electricity_available === 'Yes' ? 1 : 0,
        ict: inst.ict_lab_available === 'Yes' ? 1 : 0,
        genderToilets: inst.separate_gender_toilets === 'Yes' ? 1 : 0,
        disabilityAccess: inst.disability_accessible_toilets === 'Yes' ? 1 : 0,
        handwashing: inst.handwashing_available === 'Yes' ? 1 : 0,
      }

      const score =
        (Object.values(components).reduce((a, b) => a + b) / 6) * 100

      return {
        name: inst.institution_name || inst.name,
        water: components.water,
        electricity: components.electricity,
        ict: components.ict,
        genderToilets: components.genderToilets,
        disabilityAccess: components.disabilityAccess,
        handwashing: components.handwashing,
        score: score,
        type: inst.institution_type,
        ownership: inst.ownership,
      }
    })
    .sort((a, b) => a.score - b.score)
}

// Calculate WASH compliance metrics
export function calculateWASHCompliance(institutions) {
  if (!institutions.length) return null

  const total = institutions.length
  const metrics = {
    water: institutions.filter(inst => inst.water_available === 'Yes').length,
    handwashing: institutions.filter(
      inst => inst.handwashing_available === 'Yes'
    ).length,
    genderToilets: institutions.filter(
      inst => inst.separate_gender_toilets === 'Yes'
    ).length,
  }

  return {
    water: {
      count: metrics.water,
      percentage: Math.round((metrics.water / total) * 100),
      total: total,
    },
    handwashing: {
      count: metrics.handwashing,
      percentage: Math.round((metrics.handwashing / total) * 100),
      total: total,
    },
    genderToilets: {
      count: metrics.genderToilets,
      percentage: Math.round((metrics.genderToilets / total) * 100),
      total: total,
    },
  }
}

// Create WASH compliance progress chart
export function createWASHComplianceOption(washData) {
  if (!washData) return null

  const data = [
    {
      name: 'Water Access',
      value: washData.water.percentage,
      count: washData.water.count,
    },
    {
      name: 'Handwashing Facilities',
      value: washData.handwashing.percentage,
      count: washData.handwashing.count,
    },
    {
      name: 'Gender-Separated Toilets',
      value: washData.genderToilets.percentage,
      count: washData.genderToilets.count,
    },
  ]

  return {
    title: {
      show: false,
      text: 'WASH Compliance Overview',
      left: 'center',
      top: '5%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1F2937',
        fontFamily: 'Poppins, sans-serif',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        const data = params[0]
        const item = params[0].data
        return `
          <div style="font-family: Poppins, sans-serif;">
            <strong>${data.name}</strong><br/>
            Coverage: ${item.value}%<br/>
            Schools: ${item.count} of ${washData.water.total}<br/>
            Status: ${item.value >= 80 ? 'Good' : 'Needs Improvement'}
          </div>
        `
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      top: '20%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: data.map(item => item.name),
      axisLabel: {
        interval: 0,
        rotate: 0,
        fontSize: 12,
        fontFamily: 'Poppins, sans-serif',
      },
    },
    yAxis: {
      type: 'value',
      name: 'Coverage (%)',
      max: 100,
      nameTextStyle: {
        fontFamily: 'Poppins, sans-serif',
      },
      axisLabel: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    series: [
      {
        type: 'bar',
        data: data.map(item => ({
          value: item.value,
          count: item.count,
          itemStyle: {
            color: item.value >= 80 ? '#10B981' : '#F59E0B',
          },
        })),
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%',
          fontFamily: 'Poppins, sans-serif',
        },
        markLine: {
          data: [
            {
              yAxis: 80,
              lineStyle: { color: '#DC2626', type: 'dashed' },
              label: { formatter: 'Target: 80%' },
            },
          ],
        },
      },
    ],
  }
}

// Calculate infrastructure gap matrix data for heatmap visualization
export function calculateInfrastructureGapMatrix(institutions) {
  if (!institutions.length) return null

  const infrastructureCategories = [
    { key: 'water_available', name: 'Water' },
    { key: 'electricity_available', name: 'Electricity' },
    { key: 'ict_lab_available', name: 'ICT Lab' },
    { key: 'separate_gender_toilets', name: 'Gender Toilets' },
    { key: 'disability_accessible_toilets', name: 'Disability Access' },
    { key: 'handwashing_available', name: 'Handwashing' },
  ]

  const matrix = institutions
    .filter(inst => inst.institution_name || inst.name)
    .slice(0, 20) // Limit to first 20 schools for readability
    .map(inst => {
      const schoolData = {
        name:
          (inst.institution_name || inst.name).length > 25
            ? (inst.institution_name || inst.name).substring(0, 25) + '...'
            : inst.institution_name || inst.name,
        fullName: inst.institution_name || inst.name,
        type: inst.institution_type,
        ownership: inst.ownership,
      }

      infrastructureCategories.forEach(category => {
        schoolData[category.key] = inst[category.key] === 'Yes' ? 1 : 0
      })

      // Calculate completeness score
      const score =
        (infrastructureCategories.reduce(
          (acc, cat) => acc + schoolData[cat.key],
          0
        ) /
          infrastructureCategories.length) *
        100
      schoolData.completeness = Math.round(score)

      return schoolData
    })
    .sort((a, b) => b.completeness - a.completeness) // Sort by completeness

  return {
    schools: matrix,
    categories: infrastructureCategories,
    summary: {
      totalSchools: matrix.length,
      avgCompleteness: Math.round(
        matrix.reduce((acc, school) => acc + school.completeness, 0) /
          matrix.length
      ),
      bestSchool: matrix[0]?.name || 'None',
      worstSchool: matrix[matrix.length - 1]?.name || 'None',
    },
  }
}
